// Schema.org JSON-LD generators for SEO.
// Each function returns a plain object — caller stringifies into a <script type="application/ld+json"> tag.
// Verified via Google Rich Results Test: https://search.google.com/test/rich-results
//
// Rules:
// - All URLs must be absolute (https://choosemycollege.in/...)
// - No Date objects — JSON.stringify ignores them
// - Don't call with null/undefined data — guard at call site

interface District { id: number; name: string; }

interface College {
  id: number;
  college_code: string;
  college_name: string;
  college_type: string;
  address: string;
  district: District;
  pincode?: string;
  phone?: string;
  email?: string;
  website?: string;
  naac_grade?: string;
  autonomous_status?: boolean;
  boys_accommodation_available?: boolean;
  girls_accommodation_available?: boolean;
  latitude?: number;
  longitude?: number;
}

interface Course {
  id: number;
  branch_code: string;
  branch_name: string;
  degree_type: string;
  approved_intake_2025: number;
  total_seats: number;
  nba_accredited?: boolean;
}

// ── 1. CollegeOrUniversity ─────────────────────────────────────────────────

export function buildCollegeSchema(college: College, courses: Course[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'CollegeOrUniversity',
    '@id': `https://choosemycollege.in/colleges/${college.college_code}#college`,
    name: college.college_name,
    url: `https://choosemycollege.in/colleges/${college.college_code}`,
    address: {
      '@type': 'PostalAddress',
      streetAddress: college.address,
      addressLocality: college.district?.name,
      addressRegion: 'Tamil Nadu',
      postalCode: college.pincode,
      addressCountry: 'IN',
    },
    ...(college.latitude && college.longitude ? {
      geo: {
        '@type': 'GeoCoordinates',
        latitude: Number(college.latitude),
        longitude: Number(college.longitude),
      },
    } : {}),
    ...(college.phone ? { telephone: college.phone } : {}),
    ...(college.email ? { email: college.email } : {}),
    ...(college.website ? { sameAs: [college.website] } : {}),
    description: `${college.college_name} is a ${college.college_type} engineering college in ${college.district?.name}, Tamil Nadu offering ${courses.length} branches. NAAC Grade: ${college.naac_grade || 'N/A'}.`,
    department: courses.slice(0, 30).map(c => ({
      '@type': 'Organization',
      name: `Department of ${c.branch_name}`,
    })),
  };
}

// ── 2. Dataset (cutoff table) ──────────────────────────────────────────────

export function buildCutoffDatasetSchema(college: College, courses: Course[], year: number) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Dataset',
    name: `${college.college_name} TNEA ${year} Cutoff Marks`,
    description: `Tamil Nadu Engineering Admissions ${year} closing ranks and cutoff marks for ${college.college_name} across all communities (OC, BC, BCM, MBC, SC, SCA, ST) and branches.`,
    url: `https://choosemycollege.in/colleges/${college.college_code}`,
    keywords: [
      `${college.college_name} cutoff`,
      `TNEA ${year} cutoff`,
      `${college.college_name} TNEA ${year}`,
      ...courses.slice(0, 5).map(c => `${c.branch_name} cutoff`),
    ],
    creator: {
      '@type': 'Organization',
      name: 'ChooseMyCollege',
      url: 'https://choosemycollege.in',
    },
    temporalCoverage: `${year - 5}/${year}`,
    spatialCoverage: { '@type': 'Place', name: 'Tamil Nadu, India' },
    distribution: {
      '@type': 'DataDownload',
      encodingFormat: 'text/html',
      contentUrl: `https://choosemycollege.in/colleges/${college.college_code}`,
    },
  };
}

// ── 3. FAQPage ─────────────────────────────────────────────────────────────

export function buildFAQSchema(college: College, topCourses: Course[], year: number) {
  const naac = college.naac_grade || 'Not accredited';
  const district = college.district?.name || 'Tamil Nadu';
  const topBranch = topCourses[0]?.branch_name || 'Engineering';

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: `What is ${college.college_name} TNEA ${year} cutoff?`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `${college.college_name} accepts admissions through TNEA ${year}. Cutoff varies by branch and community. For OC community in ${topBranch}, the typical closing rank is shown on this page. See the full community-wise cutoff table for all branches.`,
        },
      },
      {
        '@type': 'Question',
        name: `Where is ${college.college_name} located?`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `${college.college_name} is located in ${district}, Tamil Nadu. ${college.address || ''}`.trim(),
        },
      },
      {
        '@type': 'Question',
        name: `What is the NAAC grade of ${college.college_name}?`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `${college.college_name} has a NAAC grade of ${naac}.`,
        },
      },
      {
        '@type': 'Question',
        name: `Is ${college.college_name} a government or private college?`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `${college.college_name} is a ${college.college_type} college.`,
        },
      },
      {
        '@type': 'Question',
        name: `Does ${college.college_name} provide hostel facilities?`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `${college.college_name} ${college.boys_accommodation_available ? 'provides boys hostel' : 'does not have boys hostel'} and ${college.girls_accommodation_available ? 'provides girls hostel' : 'does not have girls hostel'}.`,
        },
      },
    ],
  };
}

// ── 4. BreadcrumbList ──────────────────────────────────────────────────────

export function buildBreadcrumbSchema(items: Array<{ name: string; url: string }>) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

// ── 5. Organization ────────────────────────────────────────────────────────

export function buildOrganizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'ChooseMyCollege',
    url: 'https://choosemycollege.in',
    logo: {
      '@type': 'ImageObject',
      url: 'https://choosemycollege.in/icon-512.png',
      width: 512,
      height: 512,
    },
    description: 'TNEA counselling guidance platform for Tamil Nadu engineering admissions. College predictor, cutoff comparison, district rankings, and ChoiceList builder.',
  };
}

// ── 6. WebSite (enables Google sitelinks search box) ──────────────────────

export function buildWebSiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'ChooseMyCollege',
    url: 'https://choosemycollege.in',
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: 'https://choosemycollege.in/colleges?search={search_term_string}',
      },
      'query-input': 'required name=search_term_string',
    },
  };
}

// ── 7. General TNEA FAQPage (for Home page only) ──────────────────────────
// Moved out of index.html so it does not conflict with the college-specific
// FAQPage on /colleges/:code pages. Only rendered by Home.tsx.

export function buildTNEAFAQSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'How is TNEA cutoff calculated?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'TNEA cutoff = (Maths marks × 1) + (Physics marks × 0.5) + (Chemistry marks × 0.5). Maximum cutoff is 200. Students are ranked statewide based on this score and community.',
        },
      },
      {
        '@type': 'Question',
        name: 'What is the fee for government engineering colleges in Tamil Nadu?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Government engineering colleges in Tamil Nadu charge approximately ₹6,780 per year as annual tuition fee, fixed by the Tamil Nadu Fee Fixation Committee.',
        },
      },
      {
        '@type': 'Question',
        name: 'How many engineering colleges are there in Tamil Nadu under TNEA?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'There are 476 active engineering colleges participating in TNEA counselling across Tamil Nadu, including government, Anna University, aided, and private colleges across all 38 districts.',
        },
      },
      {
        '@type': 'Question',
        name: 'What communities are considered in TNEA counselling?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'TNEA counselling considers 7 community categories: OC, BC, BCM, MBC, SC, SCA, and ST. Each community has separate cutoff marks and seat reservations.',
        },
      },
    ],
  };
}

// ── 8. TNEA 2026 Event schedule (confirmed dates only — G–L are TBD) ──────

export function buildTNEAScheduleSchema() {
  const base = {
    eventStatus: 'https://schema.org/EventScheduled',
    eventAttendanceMode: 'https://schema.org/OnlineEventAttendanceMode',
    location: { '@type': 'VirtualLocation', url: 'https://tneaonline.org' },
    organizer: {
      '@type': 'Organization',
      name: 'Directorate of Technical Education (DOTE), Tamil Nadu',
      url: 'https://tneaonline.org',
    },
  };
  return [
    { name: 'TNEA 2026 Registration & Application Filing', startDate: '2026-05-03', endDate: '2026-05-03' },
    { name: 'TNEA 2026 Last Date for Online Registration', startDate: '2026-06-02', endDate: '2026-06-02' },
    { name: 'TNEA 2026 Last Date for Uploading Documents', startDate: '2026-06-06', endDate: '2026-06-06' },
    { name: 'TNEA 2026 Random Number Assignment',          startDate: '2026-06-05', endDate: '2026-06-05' },
    { name: 'TNEA 2026 Certificate Verification (Online)', startDate: '2026-06-08', endDate: '2026-06-20' },
    { name: 'TNEA 2026 Rank List Publication',             startDate: '2026-06-29', endDate: '2026-06-29' },
    { name: 'TNEA 2026 Grievances Redressal',              startDate: '2026-06-30', endDate: '2026-07-04' },
  ].map(evt => ({
    '@context': 'https://schema.org',
    '@type': 'Event',
    ...base,
    ...evt,
    description: `${evt.name} — Tamil Nadu Engineering Admissions 2026`,
  }));
}

// ── 9. ItemList (for Best Colleges rankings page) ─────────────────────────

interface RankedCollege {
  college_id: number;
  college_name: string;
  college_code: string;
  district_name: string;
  avg_closing_rank: number;
}

export function buildItemListSchema(colleges: RankedCollege[], listName: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: listName,
    description: `Top engineering colleges in Tamil Nadu ranked by TNEA closing rank.`,
    url: 'https://choosemycollege.in/best-colleges',
    numberOfItems: colleges.length,
    itemListElement: colleges.map((col, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: col.college_name,
      url: `https://choosemycollege.in/colleges/${col.college_code}`,
      description: `${col.college_name}, ${col.district_name} — Avg closing rank: ${col.avg_closing_rank}`,
    })),
  };
}
